import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { ProcessDto, TagDto, UpdateProcessDto, TaskDto, PaginatedProcessTasksDto } from '@prometeus/contracts';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { TaskFormModal } from '../components/TaskFormModal';
import { ProcessLinksList } from '../components/ProcessLinksList';
import { ArchivedProcessBlockModal } from '../components/ArchivedProcessBlockModal';
import {
  ProcessTaskListCard,
  taskDtoToCardDate,
  getTaskTypeColor,
  getTaskTypeLabel,
  getTaskStatusColor,
  getTaskStatusLabel,
  getTaskPriorityColor,
  getTaskPriorityLabel,
} from '../components/process/ProcessTaskListCard';

export const Route = createFileRoute('/w/$workspaceId/clients/$clientId/processes/$processId/')({
  component: ProcessInfoTab,
});

function ProcessInfoTab() {
  const { workspaceId, clientId, processId } = Route.useParams();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskDto | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTaskFormModal, setShowTaskFormModal] = useState(false);
  const [showArchivedBlockModal, setShowArchivedBlockModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 5;

  const canUpdate = hasPermission('processes.update');

  const { data: process, isLoading } = useQuery<ProcessDto>({
    queryKey: ['processes', workspaceId, clientId, processId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/processes/${processId}`);
      return response.data.data;
    },
  });

  const { data: allTags } = useQuery<TagDto[]>({
    queryKey: ['tags', workspaceId],
    queryFn: async () => {
      const response = await api.get('/tags');
      return response.data.data || [];
    },
    enabled: !!workspaceId,
  });

  const {
    data: upcomingPage,
    isFetching: isFetchingUpcoming,
    isLoading: isLoadingUpcoming,
  } = useQuery<PaginatedProcessTasksDto>({
    queryKey: ['tasks', 'process', processId, 'upcoming', currentPage],
    queryFn: async () => {
      const response = await api.get(`/processes/${processId}/tasks`, {
        params: {
          page: currentPage,
          limit: tasksPerPage,
          upcomingOnly: true,
        },
      });
      const d = response.data.data;
      if (d && typeof d === 'object' && Array.isArray((d as PaginatedProcessTasksDto).items)) {
        return d as PaginatedProcessTasksDto;
      }
      throw new Error('Resposta de tarefas inválida');
    },
    enabled: !!processId,
    placeholderData: (previousData) => previousData,
  });

  const activeTasks = upcomingPage?.items ?? [];
  const totalCount = upcomingPage?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / tasksPerPage));

  const rangeFrom = totalCount === 0 ? 0 : (currentPage - 1) * tasksPerPage + 1;
  const rangeTo = totalCount === 0 ? 0 : Math.min(currentPage * tasksPerPage, totalCount);

  // Inicializar selectedTagIds quando process carregar
  useEffect(() => {
    if (process?.tags && !isEditingTags) {
      setSelectedTagIds(process.tags.map((tag) => tag.id));
    }
  }, [process?.tags, isEditingTags]);

  useEffect(() => {
    setCurrentPage(1);
  }, [processId]);

  useEffect(() => {
    // Sem dados ainda (refetch) totalPages pode virar 1 e desfazer a troca de página
    if (upcomingPage == null) return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, upcomingPage]);

  const updateTagsMutation = useMutation({
    mutationFn: async (tagIds: string[]) => {
      const updateDto: UpdateProcessDto = { tagIds };
      const response = await api.put(`/clients/${clientId}/processes/${processId}`, updateDto);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes', workspaceId, clientId, processId] });
      setIsEditingTags(false);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar tags:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar tags');
    },
  });

  const handleSaveTags = () => {
    updateTagsMutation.mutate(selectedTagIds);
  };

  const handleCancelEditTags = () => {
    if (process?.tags) {
      setSelectedTagIds(process.tags.map((tag) => tag.id));
    }
    setIsEditingTags(false);
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (isLoading) {
    return (
      <div className="py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
          <p className="mt-4 text-gray-500">Carregando informações do processo...</p>
        </div>
      </div>
    );
  }

  if (!process) {
    return (
      <div className="py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">Processo não encontrado</p>
        </div>
      </div>
    );
  }

  const formatTaskDate = (date: Date | string | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  };

  const formatTaskDateTime = (date: Date | string | null) => {
    if (!date) return null;
    const d = new Date(date);
    const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    const h = d.getHours();
    const m = d.getMinutes();
    return `${dateStr} às ${h}h${m.toString().padStart(2, '0')}`;
  };

  const isCaso = process?.type === 'caso';

  return (
    <div className="pt-4 pb-8 overflow-visible">
      <div className="flex gap-4 mt-0 mb-4 items-stretch overflow-visible">
        {/* Container de Informações do processo */}
        <div
          className="flex-[2] bg-white p-4 flex flex-col"
          style={{
            fontFamily: 'Poppins, sans-serif',
            borderRadius: 15,
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
            minHeight: '600px',
          }}
        >
          {/* Header com ícone */}
          <div className="flex items-center gap-2 mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path opacity="0.3" d="M19.8199 6C20.1103 6.00011 20.3972 6.06347 20.6607 6.18567C20.9242 6.30787 21.1578 6.48598 21.3455 6.70762C21.5332 6.92926 21.6703 7.18911 21.7474 7.46911C21.8245 7.74911 21.8397 8.04254 21.7919 8.329L20.1259 18.329C20.048 18.796 19.807 19.2202 19.4457 19.5262C19.0845 19.8322 18.6263 20.0001 18.1529 20H5.84691C5.37347 20.0001 4.91536 19.8322 4.55409 19.5262C4.19281 19.2202 3.95179 18.796 3.87391 18.329L2.20791 8.329C2.16014 8.04254 2.17532 7.74911 2.25241 7.46911C2.3295 7.18911 2.46665 6.92926 2.65431 6.70762C2.84198 6.48598 3.07566 6.30787 3.33912 6.18567C3.60258 6.06347 3.88949 6.00011 4.17991 6H19.8199Z" fill="#06141B"/>
              <path d="M18 3C18.2652 3 18.5196 3.10536 18.7071 3.29289C18.8946 3.48043 19 3.73478 19 4C19 4.26522 18.8946 4.51957 18.7071 4.70711C18.5196 4.89464 18.2652 5 18 5H6C5.73478 5 5.48043 4.89464 5.29289 4.70711C5.10536 4.51957 5 4.26522 5 4C5 3.73478 5.10536 3.48043 5.29289 3.29289C5.48043 3.10536 5.73478 3 6 3H18Z" fill="#06141B"/>
            </svg>
            <h2 className="text-[16px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#06141B' }}>{isCaso ? 'Informações do caso' : 'Informações do processo'}</h2>
          </div>
          
          {/* Process Overview Box - Caixa cinza clara com informações principais */}
          <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#F9FAFB', borderRadius: 10 }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Coluna Esquerda */}
              <div className="space-y-3">
                {process.internalTitle && (
                  <div>
                    <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                      <span className="font-medium">{isCaso ? 'Nome do caso:' : 'Nome da Ação:'}</span> {process.internalTitle}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                    <span className="font-medium">{isCaso ? 'Número do caso:' : 'Número do processo:'}</span> {process.processNumber}
                  </p>
                </div>
                {process.caseValue !== null && (
                  <div>
                    <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                      <span className="font-medium">Valor da causa:</span> {formatCurrency(process.caseValue)}
                    </p>
                  </div>
                )}
                {process.court && (
                  <div>
                    <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                      <span className="font-medium">Orgão:</span> {process.court}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Coluna Direita */}
              <div className="space-y-3">
                {process.createdAt && (
                  <div>
                    <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                      <span className="font-medium">Cadastrado em:</span> {formatDate(process.createdAt)}
                    </p>
                  </div>
                )}
                {process.distributionDate && (
                  <div>
                    <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                      <span className="font-medium">Distribuição em:</span> {formatDate(process.distributionDate)}
                    </p>
                  </div>
                )}
                {process.citationDate && (
                  <div>
                    <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                      <span className="font-medium">Citação em:</span> {formatDate(process.citationDate)}
                    </p>
                  </div>
                )}
                {process.updatedAt && (
                  <div>
                    <p className="text-[12px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#2D3748' }}>
                      <span className="font-medium">Última atualização:</span> <span className="font-semibold">{formatDate(process.updatedAt)}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Conteúdo abaixo da caixa */}
          <div className="space-y-4">
            {/* Síntese do Processo */}
            {process.summary && (
              <div className="flex flex-col min-h-0">
                <p className="mb-2 flex-shrink-0" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>{isCaso ? 'Síntese do caso:' : 'Síntese do processo:'}</p>
                <div className="overflow-y-auto flex-1 min-h-0 max-h-[140px] rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                  <p className="whitespace-pre-wrap p-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400, color: '#2D3748' }}>{process.summary}</p>
                </div>
              </div>
            )}

            {/* Objeto da Ação */}
            {process.actionObject && (
              <div className="flex flex-col min-h-0">
                <p className="mb-2 flex-shrink-0" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>Objeto da ação:</p>
                <div className="overflow-y-auto flex-1 min-h-0 max-h-[140px] rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                  <p className="whitespace-pre-wrap p-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400, color: '#2D3748' }}>{process.actionObject}</p>
                </div>
              </div>
            )}

            {/* Partes do Processo */}
            {process.parts && process.parts.length > 0 ? (
              <div>
                <p className="mb-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>{isCaso ? 'Partes do caso:' : 'Partes do processo:'}</p>
                <div className="flex flex-wrap gap-2">
                  {process.parts.map((part) => (
                    <div
                      key={part.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '12px',
                        fontWeight: 400,
                        backgroundColor: '#F9FAFB',
                        color: '#2D3748',
                      }}
                    >
                      <span>{part.name}</span>
                      <div className="flex gap-1">
                        {part.pole && (
                          <span
                            className="px-2 py-0.5 rounded-lg text-[10px]"
                            style={{
                              backgroundColor: '#F9FAFB',
                              color: '#2D3748',
                            }}
                          >
                            {part.pole === 'ativo' ? 'Cliente' : part.pole === 'passivo' ? 'Passivo' : part.pole}
                          </span>
                        )}
                        {process.status === 'ativo' || process.status === 'Ativo' ? (
                          <span
                            className="px-2 py-0.5 rounded-lg text-[10px]"
                            style={{
                              backgroundColor: '#F9FAFB',
                              color: '#2D3748',
                            }}
                          >
                            Ativo
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : process.parties ? (
              <div>
                <p className="mb-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>{isCaso ? 'Partes do caso:' : 'Partes do processo:'}</p>
                <p className="whitespace-pre-wrap" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400, color: '#2D3748' }}>{process.parties}</p>
              </div>
            ) : null}

            {/* Informações de Arquivamento - Se arquivado */}
            {process.archivedAt && (
              <div>
                <div className="border-t border-gray-200 pt-6 mt-6 mb-4">
                  <h3 className="font-semibold mb-4" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#2D3748' }}>Informações de Arquivamento</h3>
                </div>
                <div>
                  <p className="mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#718096' }}>Data de Arquivamento</p>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2D3748' }}>{formatDate(process.archivedAt)}</p>
                </div>
                {process.archivedBy && (
                  <div>
                    <p className="mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#718096' }}>Arquivado por</p>
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2D3748' }}>{process.archivedBy.name}</p>
                  </div>
                )}
                {process.conclusionType && (
                  <div>
                    <p className="mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#718096' }}>Tipo de Conclusão</p>
                    <p className="capitalize" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2D3748' }}>{process.conclusionType}</p>
                  </div>
                )}
                {process.conclusionNotes && (
                  <div>
                    <p className="mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#718096' }}>Observações</p>
                    <p className="whitespace-pre-wrap" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2D3748' }}>{process.conclusionNotes}</p>
                  </div>
                )}
                {process.condemnationValue != null && (
                  <div>
                    <p className="mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, fontWeight: 500, color: '#4A5C6A' }}>Valor da Condenação</p>
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#06141B' }}>
                      {formatCurrency(process.condemnationValue)}
                    </p>
                  </div>
                )}
                {process.updatedCaseValue != null && (
                  <div>
                    <p className="mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, fontWeight: 500, color: '#4A5C6A' }}>Valor da Causa Atualizado</p>
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#06141B' }}>
                      {formatCurrency(process.updatedCaseValue)}
                    </p>
                  </div>
                )}
                {process.economicBenefitValue != null && (
                  <div>
                    <p className="mb-1" style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, fontWeight: 500, color: '#4A5C6A' }}>Valor do Proveito Econômico</p>
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#06141B' }}>
                      {formatCurrency(process.economicBenefitValue)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tags */}
            <div>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="mb-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>Tags:</p>
                  {!isEditingTags ? (
                    <div>
                      {process.tags && process.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {process.tags.map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2.5 py-1 rounded-[10px]"
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: 10,
                                fontWeight: 400,
                                backgroundColor: `${tag.color}20`,
                                color: tag.color,
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#9BA8AB' }}>Nenhuma tag atribuída</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {allTags && allTags.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {allTags.map((tag) => {
                            const isSelected = selectedTagIds.includes(tag.id);
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.id)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[10px] transition-colors"
                                style={{
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: 10,
                                  fontWeight: 400,
                                  ...(isSelected
                                    ? { backgroundColor: `${tag.color}20`, color: tag.color }
                                    : { backgroundColor: '#F9FAFB', color: '#2D3748', border: '1px solid rgba(6, 20, 27, 0.15)' }),
                                }}
                              >
                                {isSelected && (
                                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                                )}
                                {tag.name}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#9BA8AB' }}>Nenhuma tag disponível</p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={handleSaveTags}
                          disabled={updateTagsMutation.isPending}
                          className="px-4 py-2 text-sm transition-colors disabled:opacity-50"
                          style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '12px',
                            backgroundColor: '#06141B',
                            color: '#F9FAFB',
                            borderRadius: 10,
                          }}
                        >
                          {updateTagsMutation.isPending ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEditTags}
                          disabled={updateTagsMutation.isPending}
                          className="px-4 py-2 text-sm transition-colors"
                          style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '12px',
                            backgroundColor: '#F9FAFB',
                            color: '#06141B',
                            border: '1px solid #06141B',
                            borderRadius: 10,
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {canUpdate && !isEditingTags && (
                  <button
                    type="button"
                    onClick={() => {
                      if (process?.archivedAt) {
                        setShowArchivedBlockModal(true);
                        return;
                      }
                      if (process?.tags) {
                        setSelectedTagIds(process.tags.map((tag) => tag.id));
                      }
                      setIsEditingTags(true);
                    }}
                    className="ml-2 flex items-center justify-center w-8 h-8 rounded-[10px] transition-opacity hover:opacity-90 flex-shrink-0"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 18,
                      fontWeight: 400,
                      lineHeight: 1,
                      backgroundColor: '#06141B',
                      color: '#F9FAFB',
                    }}
                    title="Editar tags"
                    aria-label="Editar tags"
                  >
                    +
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Próximas tarefas */}
        <div
          className="flex-[1.5] bg-white p-4 flex flex-col"
          style={{
            fontFamily: 'Poppins, sans-serif',
            borderRadius: 15,
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
            minHeight: '600px',
            maxHeight: '800px',
            overflow: 'hidden',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.3" d="M7 3V4C7 4.53043 7.21071 5.03914 7.58579 5.41421C7.96086 5.78929 8.46957 6 9 6H15C15.5304 6 16.0391 5.78929 16.4142 5.41421C16.7893 5.03914 17 4.53043 17 4V3H18C18.5304 3 19.0391 3.21071 19.4142 3.58579C19.7893 3.96086 20 4.46957 20 5V16C20 17.5913 19.3679 19.1174 18.2426 20.2426C17.1174 21.3679 15.5913 22 14 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V5C4 4.46957 4.21071 3.96086 4.58579 3.58579C4.96086 3.21071 5.46957 3 6 3H7Z" fill="#06141B"/>
                <path d="M14 2C14.2549 2.00028 14.5 2.09788 14.6854 2.27285C14.8707 2.44782 14.9822 2.68695 14.9972 2.94139C15.0121 3.19584 14.9293 3.44638 14.7657 3.64183C14.6021 3.83729 14.3701 3.9629 14.117 3.993L14 4H10C9.74512 3.99972 9.49997 3.90212 9.31463 3.72715C9.1293 3.55218 9.01776 3.31305 9.00283 3.05861C8.98789 2.80416 9.07067 2.55362 9.23426 2.35817C9.39785 2.16271 9.6299 2.0371 9.883 2.007L10 2H14ZM15 10H9C8.74512 10.0003 8.49997 10.0979 8.31463 10.2728C8.1293 10.4478 8.01776 10.687 8.00283 10.9414C7.98789 11.1958 8.07067 11.4464 8.23426 11.6418C8.39785 11.8373 8.6299 11.9629 8.883 11.993L9 12H15C15.2652 12 15.5196 11.8946 15.7071 11.7071C15.8946 11.5196 16 11.2652 16 11C16 10.7348 15.8946 10.4804 15.7071 10.2929C15.5196 10.1054 15.2652 10 15 10ZM12 14H9C8.73478 14 8.48043 14.1054 8.29289 14.2929C8.10536 14.4804 8 14.7348 8 15C8 15.2652 8.10536 15.5196 8.29289 15.7071C8.48043 15.8946 8.73478 16 9 16H12C12.2652 16 12.5196 15.8946 12.7071 15.7071C12.8946 15.5196 13 15.2652 13 15C13 14.7348 12.8946 14.4804 12.7071 14.2929C12.5196 14.1054 12.2652 14 12 14Z" fill="#06141B"/>
              </svg>
              <h2 className="text-[16px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#06141B' }}>
                Próximas tarefas
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (process?.archivedAt) {
                    setShowArchivedBlockModal(true);
                    return;
                  }
                  if (!requirePermission('tasks.create', 'Criar tarefas')) return;
                  setShowTaskFormModal(true);
                }}
                className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:opacity-90"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  backgroundColor: '#06141B',
                  color: '#F9FAFB',
                  borderRadius: 10,
                }}
                title="Nova tarefa"
                aria-label="Adicionar tarefa"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div
            className={`flex-1 min-h-0 overflow-y-auto space-y-3 px-3 pt-2 relative transition-opacity ${isFetchingUpcoming ? 'opacity-70' : ''}`}
          >
            {isLoadingUpcoming ? (
              <div className="space-y-3 py-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-[72px] rounded-[10px] animate-pulse"
                    style={{ backgroundColor: 'rgba(6, 20, 27, 0.06)' }}
                  />
                ))}
              </div>
            ) : activeTasks.length > 0 ? (
              activeTasks.map((task) => {
                const pri = getTaskPriorityLabel(task.priority);
                return (
                  <ProcessTaskListCard
                    key={task.id}
                    typeLabel={getTaskTypeLabel(task.type)}
                    typeColor={getTaskTypeColor(task.type)}
                    title={task.title}
                    assigneeDisplay={task.assignee?.userName ?? undefined}
                    dateCornerLabel={taskDtoToCardDate(task) ?? undefined}
                    badges={[
                      {
                        label: getTaskStatusLabel(task.status),
                        color: getTaskStatusColor(task.status),
                      },
                      ...(pri ? [{ label: pri, color: getTaskPriorityColor(task.priority) }] : []),
                    ]}
                    onClick={() => {
                      if (!requirePermission('tasks.read', 'Ver tarefas')) return;
                      setSelectedTask(task);
                      setShowDetailModal(true);
                    }}
                  />
                );
              })
            ) : (
              <p 
                className="text-center py-8 text-sm"
                style={{ 
                  fontFamily: 'Poppins, sans-serif', 
                  color: '#A0AEC0' 
                }}
              >
                Nenhuma próxima tarefa
              </p>
            )}
          </div>

          {/* Paginação: apenas os itens da página atual (5) — sem scroll da lista inteira */}
          {totalCount > tasksPerPage && (
            <div className="flex flex-col items-center gap-2 pt-3 flex-shrink-0 mt-auto">
              <span
                className="text-[11px]"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  color: '#718096',
                }}
              >
                {rangeFrom}–{rangeTo} de {totalCount}
              </span>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    backgroundColor: currentPage === 1 ? '#F9FAFB' : '#06141B',
                    color: currentPage === 1 ? '#9BA8AB' : '#F9FAFB',
                    borderRadius: 10,
                  }}
                  aria-label="Página anterior"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <span
                  className="text-[12px] min-w-[7rem] text-center"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    color: '#2D3748',
                  }}
                >
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    backgroundColor: currentPage === totalPages ? '#F9FAFB' : '#06141B',
                    color: currentPage === totalPages ? '#9BA8AB' : '#F9FAFB',
                    borderRadius: 10,
                  }}
                  aria-label="Próxima página"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Processos Vinculados */}
      <ProcessLinksList
        workspaceId={workspaceId}
        clientId={clientId}
        processId={processId}
        linkedProcesses={process?.linkedProcesses}
        isArchived={!!process?.archivedAt}
        onArchivedBlock={() => setShowArchivedBlockModal(true)}
      />

      {/* Modal de Detalhes da Tarefa */}
      {showDetailModal && selectedTask ? (
        <TaskDetailModal
          task={selectedTask!}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTask(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tasks', 'process', processId], exact: false });
          }}
        />
      ) : null}

      {/* Modal de Nova Tarefa */}
      {showTaskFormModal && (
        <TaskFormModal
          processId={processId}
          onClose={() => setShowTaskFormModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ['tasks', 'process', processId],
              exact: false,
            });
            setShowTaskFormModal(false);
          }}
        />
      )}
      {/* Modal de processo arquivado (bloqueio) */}
      <ArchivedProcessBlockModal
        isOpen={showArchivedBlockModal}
        onClose={() => setShowArchivedBlockModal(false)}
        workspaceId={workspaceId}
        clientId={clientId}
        processId={processId}
      />
    </div>
  );
}

